import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { useGetParams } from './hooks/useGetParams';
import { useBroadcast } from './hooks/useBroadcast';
import { useNoiseLevel } from './hooks/useNoiseLevel';

import { NoizeValue } from './components/NoizeValue/NoizeValue';
import NoiseMeter from './components/NoiseMeter/NoiseMeter';
import { NewRecord } from './components/NewRecord/NewRecord';
import { Player } from './components/Player/Player';
import { MatchScore } from './components/MatchScore/MatchScore';

import posterDefault from './assets/images/bgPoster.webp';
import bgDefaultVideo from './assets/video/bgVideo.webm';
import dark from './assets/images/dark.png';

const maxRecordState = 'S12';
const microphonMuterState = 'S13';

function App() {
  const { currentState, allStates, prevRecord, description } = useBroadcast();
  const [prevDelta, setPrevDelta] = useState(0);

  const {
    bgVideo,
    bgPoster,
    calibrationOffset,
    deltaDuration,
    smoothingFactor,
    maxNoiseLevel,
  } = useGetParams();

  const {
    calibratedLevelDbSPL,
    currentMicrophone,
    addSmoothBoost,
    stopMeasurement,
    startMeasurement,
    isActiveMeasurement,
    startFake,
    stopFake,
    isActiveFake,
    maxCalibratedLevelDbSPL,
  } = useNoiseLevel({
    calibrationOffset,
    deltaDuration,
    smoothingFactor,
  });

  const lastExecutedRef = useRef(0);

  useEffect(() => {
    if (currentState === maxRecordState && isActiveMeasurement) {
      stopMeasurement();
    } else if (currentState === microphonMuterState && isActiveMeasurement) {
      stopMeasurement();
    } else if (
      currentState !== microphonMuterState &&
      currentState !== maxRecordState
    ) {
      if (!isActiveMeasurement) {
        startMeasurement();
      }
    }
  }, [currentState, isActiveMeasurement]);

  useEffect(() => {
    if (currentState === maxRecordState) {
      if (isActiveFake) {
        stopFake();
      }
    } else if (currentState === microphonMuterState && !isActiveFake) {
      startFake();
      setPrevDelta(0);
      addSmoothBoost(-prevDelta);
    } else if (
      currentState !== microphonMuterState &&
      currentState !== maxRecordState
    ) {
      if (isActiveFake) {
        stopFake();
      }
    }
  }, [currentState, isActiveFake, prevDelta]);

  useEffect(() => {
    if (currentState !== 'S6') {
      return;
    }
    if (!isActiveMeasurement) {
      startMeasurement();
    }
    const nameState = Number(allStates?.[currentState]?.value || 0);

    const now = Date.now();

    if (now - lastExecutedRef.current >= Number(deltaDuration)) {
      if (currentState === 'S6' && calibratedLevelDbSPL < nameState) {
        let deltaFromName = nameState - calibratedLevelDbSPL;
        const delta = deltaFromName + prevDelta;
        setPrevDelta(delta);
        addSmoothBoost(deltaFromName);
      }
      lastExecutedRef.current = now;
    }
  }, [calibratedLevelDbSPL, currentState, allStates, isActiveMeasurement]);

  useEffect(() => {
    const deltaStates = ['S1', 'S2', 'S3', 'S4', 'S5'];

    if (!deltaStates.includes(currentState)) {
      return;
    }

    if (allStates && currentState) {
      let deltaFromName = 0;

      if (currentState === 'S1') {
        deltaFromName = 0;
      } else {
        deltaFromName = Number(allStates[currentState]?.value || 0) || 0;
      }

      if (deltaFromName !== prevDelta) {
        const delta = deltaFromName - prevDelta;

        setPrevDelta(deltaFromName);
        addSmoothBoost(delta);
      }
    }
  }, [currentState, allStates, addSmoothBoost, prevDelta]);

  useEffect(() => {
    if (currentMicrophone?.label) {
      console.log(currentMicrophone?.label);
    }
  }, [currentMicrophone]);

  return (
    <div className="app-wrapper">
      <div className="titre-wrapper">
        <div className="noize-wrapper">
          <div
            style={{
              minHeight: '13.06rem',
              position: 'relative',
              width: '100%',
            }}
          >
            <motion.div
              className="noizeValue"
              initial={{ opacity: 0 }}
              animate={{
                opacity:
                  currentState !== maxRecordState
                    ? 1
                    : [1, 0, 1, 0, 1, 0, 1, 0],
              }}
              transition={{
                duration: currentState !== maxRecordState ? 0.5 : 2,
                delay: currentState !== maxRecordState ? 0.5 : 0,
              }}
            >
              {calibratedLevelDbSPL > 0 && (
                <NoizeValue
                  noiseLevel={
                    currentState === maxRecordState
                      ? maxCalibratedLevelDbSPL
                      : calibratedLevelDbSPL
                  }
                />
              )}
            </motion.div>

            <AnimatePresence mode="wait">
              {currentState === maxRecordState && calibratedLevelDbSPL && (
                <motion.div
                  key="noizeRecord"
                  initial={{ scale: 0 }}
                  animate={{
                    scale: [0, 1, 0.7, 1, 0.7, 1],
                    transition: { duration: 6.5, delay: 2 },
                  }}
                  exit={{ scale: 0, transition: { duration: 0.5 } }}
                  className="newRecordBlock"
                >
                  <NewRecord noiseLevel={maxCalibratedLevelDbSPL} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NoiseMeter
            noiseLevel={
              currentState === maxRecordState
                ? maxCalibratedLevelDbSPL
                : calibratedLevelDbSPL
            }
            prevRecord={prevRecord}
            maxNoiseLevel={maxNoiseLevel}
            visibleRecord={currentState !== maxRecordState}
          />
        </div>
      </div>

      {bgVideo && (
        <video
          autoPlay="autoplay"
          muted="muted"
          loop
          id="bgVideo"
          poster={bgVideo === '1' ? posterDefault : bgPoster}
        >
          <source
            src={bgVideo === '1' ? bgDefaultVideo : bgVideo}
            type="video/webm"
          ></source>
        </video>
      )}
      <Player currentState={currentState} allStates={allStates} />

      <MatchScore strMatchScore={description} />

      <img src={dark} alt="darkBg" className="dark" />
    </div>
  );
}

export default App;
